import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Routes, Route, BrowserRouter, Navigate } from "react-router-dom";
import { PageLayout } from "./components/layout/page-layout";
import { RequireAuth } from "./components/auth/require-auth";
import { AuthProvider } from "./context/auth-context";
import { ProjectProvider } from "./context/project-context";
import { SiteProvider } from "./contexts/SiteContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import { ThemeProvider } from "./components/theme-provider";
import { TrackingProvider } from "./components/tracking/TrackingProvider";
import { Toaster } from "./components/ui/toaster";
import { Login } from "./components/auth/login";
import { LogoutPage } from "./components/auth/logout";
import { SignUp } from "./components/auth/signup";
import { ExternalLink } from "lucide-react";

// Log app version on startup
console.log('%c🚀 PAGEMIND ADMIN LOADED', 'background: #ff6b6b; color: white; font-size: 20px; padding: 10px; border-radius: 5px;');
console.log('%cVersion: 2024-01-23-v4-API-PATH-FIX', 'background: #4ecdc4; color: white; font-size: 16px; padding: 5px; border-radius: 3px;');
console.log('Current URL:', window.location.href);
console.log('Hostname:', window.location.hostname);

// Pages
import Home from "./pages/home";
import RoleDashboard from "./pages/RoleDashboard";
import NewHomePage from "./pages/newhome";
import PricingPage from "./pages/pricing";
import PaymentSuccessPage from "./pages/payment-success";
import AboutPage from "./pages/about";
import FeaturesPage from "./pages/features";
import VideoDemo from "./pages/video-demo";
import ContactPage from "./pages/contact";
import PartnersPage from "./pages/partners";
import PrivacyPage from "./pages/privacy";
import TermsPage from "./pages/terms";
import DocsPage from "./pages/docs";
import FeedExportApiDocs from "./pages/api-docs/feed-export";

// Waitlist Pages
import WaitlistStartPage from "./pages/waitlist/start";
import WaitlistAddSitePage from "./pages/waitlist/add-site";
import WaitlistConfirmationPage from "./pages/waitlist/waitlist-confirmation";
import AgentsPage from "./pages/agents";
import AgentAdminPage from "./pages/agents/admin";
import FixAgentsPage from "./pages/agents/fix-agents";
import PerformancePage from "./pages/agents/performance";
import MonitoringDashboard from "./pages/monitoring";
import PuppeteerGallery from "./pages/puppeteer-gallery";

// New Pages
import Dashboard from "./pages/agents/dashboard";
import AgentDetailPage from "./pages/agents/detail";

// Insights Pages
import MessagesPage from "./pages/insights/messages";
import ContentBotMessagePage from "./pages/insights/messages/contentbot";
import CompetitorBotMessagePage from "./pages/insights/messages/competitorbot";
import OnboardingBotMessagePage from "./pages/insights/messages/onboardingbot";
import ChannelsPage from "./pages/insights/channels";
import CompetitorsChannelPage from "./pages/insights/channels/competitors-intel";
import HelpNeededChannelPage from "./pages/insights/channels/help-needed";
import GrowthChannelPage from "./pages/insights/channels/growth";
import BugsChannelPage from "./pages/insights/channels/bugs";
import InsightsChannelPage from "./pages/insights/channels/insights";
import { AgentInsightsHub } from "./pages/insights/hub";
import UnifiedAgentHub from "./pages/insights/hub/UnifiedAgentHub";
import InboxPage from "./pages/insights/inbox";

// Sites Management
import SitesPage from "./pages/Sites";
import SiteDetails from "./pages/SiteDetails";
import MySites from "./pages/MySites";

// SEO Tracking
import SEODashboard from "./pages/SEODashboard";

// About the Business Pages
import TheSector from "./pages/about-business/TheSector";
import CategoryTree from "./pages/category-tree";
import TheOffering from "./pages/about-business/TheOffering";

// SEO Pages
import VisibilityDashboard from "./pages/seo/VisibilityDashboard";

// Settings Pages
import ContentConfigPage from "./pages/settings/ContentConfigPage";
import MarketsConfiguration from "./pages/settings/MarketsConfiguration";
import GoogleIntegrations from "./pages/settings/GoogleIntegrations";
import ShopifyConnect from "./pages/settings/ShopifyConnect";
import ProcessingJobs from "./pages/ProcessingJobs";
import GoogleIntelligenceDashboard from "./pages/GoogleIntelligenceDashboard";
import { AIArticles } from "./pages/ai-articles/AIArticles";
import FeedExport from "./pages/FeedExport";
import FeedImports from "./pages/FeedImports";

// Mock Pages
import MockAgentConversation from "./pages/MockAgentConversation";
import MySiteTopics from "./pages/MySiteTopics";
import MyKnowledge from "./pages/MyKnowledge";

// Topic Approval
import TopicApproval from "./pages/TopicApproval";
import SiteTopics from "./pages/SiteTopicsEnhanced"; // Enhanced version with RAG documents
import ResearchDocuments from "./pages/ResearchDocuments";
import AEOResearch from "./pages/AEOResearch";
import QuestionWheelResearch from "./pages/QuestionWheelResearch";
import QuestionWheelDebug from "./pages/QuestionWheelDebug";
import CategoryIntentClusters from "./pages/CategoryIntentClusters";
import CategoryResearch from "./pages/CategoryResearch";
import SiteDocuments from "./pages/SiteDocuments";
import DataSources from "./pages/DataSources";
import AttributeApproval from "./pages/AttributeApproval";
import EnrichmentReview from "./pages/EnrichmentReview";
import BulkCorrection from "./pages/BulkCorrection";
import DataQualityAudit from "./pages/DataQualityAudit";
import BenefitAttributeMappings from "./pages/BenefitAttributeMappings";
import BatchResearchManagement from "./pages/BatchResearchManagement";
import SectorQuestionWheels from "./pages/SectorQuestionWheels";

// Sector Management
import SectorManagementEnhanced from "./pages/SectorManagementEnhanced";
import { SectorDemandExplorer } from "./pages/SectorDemandExplorer";
import BatchQuestionGeneration from "./pages/sectors/BatchQuestionGeneration";
import BatchJobMonitor from "./pages/sectors/BatchJobMonitor";
import ProductCategoryGenerator from "./pages/sectors/ProductCategoryGenerator";
import QuestionGenerator from "./pages/sectors/QuestionGenerator";
import OperationalToolsDashboard from "./pages/sectors/OperationalToolsDashboard";
import TrendResearchTool from "./pages/sectors/TrendResearchTool";

// Agent Monitoring
import AgentMonitoring from "./pages/AgentMonitoring";
import SquadOps from "./pages/SquadOps";

// License Management
import LicenseManagement from "./pages/admin/LicenseManagement";

// Source Domain Management
import SourceDomainsManagement from "./pages/admin/SourceDomainsManagement";

// Subscription Management
import { SubscriptionManagement } from "./pages/admin/SubscriptionManagement";
import ImageGeneration from "./pages/super-admin/ImageGeneration";

// Research Management (Super Admin)
import ResearchManagement from "./pages/admin/ResearchManagement";
import AdminCategoryResearch from "./pages/admin/CategoryResearch";

// Content Interviews
import JournalistsManagement from "./pages/JournalistsManagement";
import ContentInterviews from "./pages/ContentInterviews";
import ExpertInterview from "./pages/ExpertInterview";
import ExpertsManagement from "./pages/ExpertsManagement";
import SitePermissions from "./pages/SitePermissions";

// AEO Benchmarks
import AEOBenchmarks from "./pages/AEOBenchmarks";

// AEO Impact Demo (Interactive visualization)
import AEOImpactDemo from "./pages/AEOImpactDemo";

// AEO Impact Dashboard (Measure AEO content impact)
import AEOImpactDashboard from "./pages/AEOImpactDashboard";

// Public AEO Benchmark Tool
import AEOBenchmarkPublic from "./pages/public/AEOBenchmark";

// Content Review Dashboard (Story 29)
import ContentReviewDashboard from "./pages/content-review/ContentReviewDashboard";

// Quality Audit Dashboard (Story 80)
import QualityAuditDashboard from "./pages/QualityAuditDashboard";

// Data Quality Dashboard (Unified data quality view)
import DataQualityDashboard from "./pages/DataQualityDashboard";

// Semantic Gap Queue (Technology mapping workflow)
import SemanticGapsDashboard from "./pages/SemanticGapsDashboard";

// Translations Management
import TranslationsDashboard from "./pages/TranslationsDashboard";

// Domain Knowledge Dashboard (Story 30)
import DomainKnowledgeDashboard from "./pages/domain-knowledge/DomainKnowledgeDashboard";

// Suggestions Page (Super Admin - Story 30)
import SuggestionsPage from "./pages/admin/SuggestionsPage";

// My Suggestions Page (All Users - Story 30)
import MySuggestionsPage from "./pages/MySuggestionsPage";

// Pipeline Dashboard (Story 54)
import PipelineDashboard from "./pages/pipeline/PipelineDashboard";
// Pipeline Monitor (post-PR-#950 outage; v1 polling-only)
import PipelineMonitor from "./pages/PipelineMonitor";
import RobotDiagnosticsDashboard from "./pages/robot-diagnostics/RobotDiagnosticsDashboard";
// Crawl Intelligence Dashboard (Story 56)
import CrawlIntelligenceDashboard from "./pages/CrawlIntelligenceDashboard";

// Demand Research
import DemandResearch from "./pages/DemandResearch";

// Executive Value Dashboard (Story 73) & AI Factory Dashboard (Story 74)
import ExecutiveValueDashboard from "./pages/ExecutiveValueDashboard";
import BusinessKPIDashboard from "./pages/BusinessKPIDashboard";
import AIFactoryDashboard from "./pages/AIFactoryDashboard";
import PipelineTracker from "./pages/PipelineTracker";

// Content ROI Analytics (Story 369)
import ContentROIDashboard from "./pages/ContentROIDashboard";

// Content Intelligence Dashboard
import ContentIntelligence from "./pages/ContentIntelligence";

// Content Opportunities — Consent Gate
import ContentOpportunities from "./pages/ContentOpportunities";

// Product Success Story Dashboard (Story 79)
import ProductSuccessStory from "./pages/ProductSuccessStory";

// SOV Dashboard — AI Share of Voice
import SOVDashboard from "./pages/SOVDashboard";

// Competitive Landscape — Actor model, category board, competitive position
import CompetitiveLandscape from "./pages/CompetitiveLandscape";
import ActorProfile from "./pages/ActorProfile";

// Value Evidence Dashboard — 10-dimension ROI proof
import ValueEvidenceDashboard from "./pages/ValueEvidenceDashboard";

// Deployment Verification Dashboard
import DeploymentVerificationDashboard from "./pages/DeploymentVerificationDashboard";

// Answer Generation & Knowledge Base
import AnswerGeneration from "./pages/AnswerGeneration";
import AnswerApproval from "./pages/AnswerApproval";
import AIAnswerTest from "./pages/AIAnswerTest";
import KnowledgeBase from "./pages/KnowledgeBase";
import QASystemDashboard from "./pages/QASystemDashboard";

// Demand Intelligence (Story 77)
import DemandIntelligenceDashboard from "./pages/DemandIntelligenceDashboard";

// Service Pages (admin-04)
import ServiceCatalog from "./pages/service/ServiceCatalog";
import ServiceBookings from "./pages/service/ServiceBookings";
import BookingIntegrations from "./pages/service/BookingIntegrations";
import LeadInbox from "./pages/service/LeadInbox";
import DemandSynthesisDashboard from "./pages/synthesis/DemandSynthesisDashboard";
import TerrainExplorer from "./pages/synthesis/TerrainExplorer";

// AEO Readiness Dashboard
import AEOReadinessDashboard from "./pages/AEOReadinessDashboard";

// AEO Onboarding Wizard
import AEOOnboardingWizard from "./pages/AEOOnboardingWizard";

// Analytics
import MatomoAnalytics from "./pages/analytics/matomo";
import ETLMonitoring from "./pages/analytics/etl-monitoring";
import BehaviorDashboard from "./pages/BehaviorDashboard";

// Growth Intelligence
import GrowthDashboard from "./pages/GrowthDashboard";
import ThreadDetail from "./pages/ThreadDetail";

// Conversation Intelligence
import ConversationIntelligenceDashboard from "./pages/ConversationIntelligenceDashboard";

// Crawl Monitoring
import { CrawlMonitor } from "./pages/CrawlMonitor";

// Session Replays
import SessionReplays from "./pages/SessionReplays";
import ActiveSessions from "./pages/ActiveSessions";

// Chatbot Logs
import ChatbotLogs from "./pages/ChatbotLogs";

// Chatbot Analytics
import ChatbotAnalytics from "./pages/ChatbotAnalytics";

// Chatbot Performance
import ChatbotPerformance from "./pages/ChatbotPerformance";

// Discovery Orchestrator
import DiscoveryPage from "./pages/discovery";
import SearchPerformance from "./pages/SearchPerformance";

// Customer Segments

// Test Pages (for debugging)
import TestReprocessingDialog from "./pages/TestReprocessingDialog";
import StreamingChatTest from "./pages/StreamingChatTest";
import CompetitiveBenchmarkTest from "./pages/CompetitiveBenchmarkTest";
import CustomerSegments from "./pages/analytics/customer-segments";
import SegmentManagement from "./pages/analytics/segment-management";
import SegmentAnalytics from "./pages/analytics/segment-analytics";

// Crew
import Crew from "./pages/crew";

// Account
import Account from "./pages/Account";
import Licensing from "./pages/Licensing";
import LicensingEnhanced from "./pages/LicensingEnhanced";

// Growth

// Super Admin Dashboard
import SuperAdminDashboard from "./pages/admin/Dashboard";
import DeveloperTools from "./pages/DeveloperTools";
import KafkaMonitoring from "./pages/admin/KafkaMonitoring";
import DataManagement from "./pages/DataManagement";
import CustomerSimulator from "./pages/CustomerSimulator";
import WaitlistDashboard from "./pages/admin/WaitlistDashboard";

// Agent Conversations
import AgentConversationsPage from "./pages/AgentConversations";

// Research Intelligence
import { ResearchDashboard } from "./components/research/ResearchDashboard";

// Help Center
import HelpCenter from "./pages/help";
import FAQ from "./pages/help/faq";
import UserGuides from "./pages/help/guides";
import OnboardingGuide from "./pages/help/guides/onboarding";
import CategoryResearchGuide from "./pages/help/guides/category-research";
import GoogleIntegrationsGuide from "./pages/help/guides/google-integrations";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 0, // Consider data stale immediately
      refetchOnWindowFocus: true, // Refetch when window regains focus
      refetchOnMount: true, // Always refetch when component mounts
      gcTime: 5 * 60 * 1000, // Keep cache for 5 minutes (gcTime replaces cacheTime in v5)
    },
  },
});

// AuthenticatedApp component to handle authenticated routes
const AuthenticatedApp = () => {
  return (
    <ProjectProvider>
      <SiteProvider>
        <LanguageProvider>
        <PageLayout>
          <Routes>
          <Route path="/dashboard" element={<RoleDashboard />} />
          
          {/* Agent Routes */}
          <Route path="/agents" element={<AgentsPage />} />
          <Route path="/agents/dashboard" element={<Dashboard />} />
          <Route path="/agents/detail/:id" element={<AgentDetailPage />} />
          <Route path="/agents/admin" element={<AgentAdminPage />} />
          <Route path="/agents/fix-agents" element={<FixAgentsPage />} />
          <Route path="/agents/squad-ops" element={<SquadOps />} />
          <Route path="/agents/performance" element={<PerformancePage />} />
          
          {/* Legacy URL fallback for backward compatibility */}
          <Route path="/agents/admin/performance" element={<PerformancePage />} />
          
          {/* Other Routes */}
          <Route path="/monitoring" element={<MonitoringDashboard />} />
          <Route path="/puppeteer-gallery" element={<PuppeteerGallery />} />
          
          {/* Insights Routes */}
          <Route path="/insights" element={<MessagesPage />} />
          <Route path="/insights/messages" element={<MessagesPage />} />
          <Route path="/insights/messages/contentbot" element={<ContentBotMessagePage />} />
          <Route path="/insights/messages/competitorbot" element={<CompetitorBotMessagePage />} />
          <Route path="/insights/messages/onboardingbot" element={<OnboardingBotMessagePage />} />
          <Route path="/insights/messages/:agentName" element={<CompetitorBotMessagePage />} />
          <Route path="/insights/channels" element={<ChannelsPage />} />
          <Route path="/insights/channels/competitors-intel" element={<CompetitorsChannelPage />} />
          <Route path="/insights/channels/help-needed" element={<HelpNeededChannelPage />} />
          <Route path="/insights/channels/growth" element={<GrowthChannelPage />} />
          <Route path="/insights/channels/bugs" element={<BugsChannelPage />} />
          <Route path="/insights/channels/insights" element={<InsightsChannelPage />} />
          <Route path="/insights/channels/:channelName" element={<ContentBotMessagePage />} />
          
          {/* Agent Action Inbox */}
          <Route path="/insights/inbox" element={<InboxPage />} />

          {/* Agent Insights Hub */}
          <Route path="/insights/hub/:agentId" element={<UnifiedAgentHub />} />
          
          {/* Site Owner Pages */}
          <Route path="/my-sites" element={<MySites />} />
          <Route path="/crawl-monitor" element={<CrawlMonitor />} />
          <Route path="/my-topics" element={<MySiteTopics />} />
          <Route path="/seo-tracking" element={<SEODashboard />} />
          <Route path="/seo/visibility" element={<VisibilityDashboard />} />
          <Route path="/my-knowledge" element={<MyKnowledge />} />
          <Route path="/settings/content-config" element={<ContentConfigPage />} />
          <Route path="/settings/markets" element={<MarketsConfiguration />} />
          <Route path="/settings/integrations" element={<GoogleIntegrations />} />
          <Route path="/settings/shopify" element={<ShopifyConnect />} />
          <Route path="/google-intelligence" element={<GoogleIntelligenceDashboard />} />

          {/* Demand Research */}
          <Route path="/demand-research" element={<DemandResearch />} />

          {/* About the Business */}
          <Route path="/about-business/sector" element={<TheSector />} />
          <Route path="/about-business/offering" element={<TheOffering />} />
          <Route path="/category-tree" element={<CategoryTree />} />

          {/* Service Pages (admin-04) */}
          <Route path="/service/catalog" element={<ServiceCatalog />} />
          <Route path="/service/bookings" element={<ServiceBookings />} />
          <Route path="/service/integrations" element={<BookingIntegrations />} />
          <Route path="/service/leads" element={<LeadInbox />} />

          {/* AI Articles */}
          <Route path="/ai-articles" element={<AIArticles />} />

          {/* Feed Export */}
          <Route path="/feed-export" element={<FeedExport />} />

          {/* Feed Imports */}
          <Route path="/feed-imports" element={<FeedImports />} />

          {/* Processing Jobs */}
          <Route path="/processing-jobs" element={<ProcessingJobs />} />

          {/* Super Admin Dashboard */}
          <Route path="/admin" element={<SuperAdminDashboard />} />
          <Route path="/admin/customer-simulator" element={<CustomerSimulator />} />
          
          {/* Sites Management (Super Admin) */}
          <Route path="/admin/sites" element={<SitesPage />} />
          <Route path="/sites/:siteId" element={<SiteDetails />} />
          
          {/* Topic Approval (Super Admin) */}
          <Route path="/admin/topics" element={<TopicApproval />} />
          <Route path="/site-topics" element={<SiteTopics />} />
          <Route path="/admin/research-documents" element={<ResearchDocuments />} />
          <Route path="/site-documents" element={<SiteDocuments />} />
          
          {/* Sector Management (Super Admin) */}
          <Route path="/admin/sectors" element={<SectorManagementEnhanced />} />
          <Route path="/admin/sector-demand-explorer" element={<SectorDemandExplorer />} />

          {/* Research Management (Super Admin) */}
          <Route path="/admin/research-management" element={<ResearchManagement />} />
          <Route path="/admin/category-research" element={<AdminCategoryResearch />} />

          {/* Agent Monitoring (Super Admin) */}
          <Route path="/admin/agent-monitoring" element={<AgentMonitoring />} />
          
          {/* Subscription Management (Super Admin) */}
          <Route path="/admin/subscriptions" element={<SubscriptionManagement />} />

          {/* Image Generation (Super Admin) */}
          <Route path="/admin/image-generation" element={<ImageGeneration />} />

          {/* Journalists Management (Super Admin) */}
          <Route path="/admin/journalists" element={<JournalistsManagement />} />

          {/* Content Interviews (Super Admin) */}
          <Route path="/admin/interviews" element={<ContentInterviews />} />

          {/* Experts Management (Super Admin) */}
          <Route path="/admin/experts" element={<ExpertsManagement />} />

          {/* Site Permissions Management (Super Admin) */}
          <Route path="/admin/site-permissions" element={<SitePermissions />} />

          {/* Waitlist Management (Super Admin) */}
          <Route path="/admin/waitlist" element={<WaitlistDashboard />} />

          {/* AEO Benchmarks */}
          <Route path="/aeo-benchmarks" element={<AEOBenchmarks />} />

          {/* AEO Impact Demo */}
          <Route path="/aeo-impact-demo" element={<AEOImpactDemo />} />

          {/* AEO Impact Dashboard */}
          <Route path="/aeo-impact" element={<AEOImpactDashboard />} />

          {/* Content Review Dashboard (Story 29) */}
          <Route path="/content-review" element={<ContentReviewDashboard />} />

          {/* Domain Knowledge Dashboard (Story 30) */}
          <Route path="/domain-knowledge" element={<DomainKnowledgeDashboard />} />

          {/* Suggestions Page (Super Admin - Story 30) */}
          <Route path="/admin/suggestions" element={<SuggestionsPage />} />

          {/* My Suggestions Page (All Users - Story 30) */}
          <Route path="/my-suggestions" element={<MySuggestionsPage />} />

          {/* Pipeline Dashboard (Story 54) */}
          <Route path="/pipeline" element={<PipelineDashboard />} />

          {/* Pipeline Monitor (post-PR-#950 outage; v1 polling-only) */}
          <Route path="/pipeline-monitor" element={<PipelineMonitor />} />

          {/* Robot Diagnostics (Story 55) */}
          <Route path="/robot-diagnostics" element={<RobotDiagnosticsDashboard />} />

          {/* Crawl Intelligence (Story 56) */}
          <Route path="/crawl-intelligence" element={<CrawlIntelligenceDashboard />} />

          {/* Executive Value Dashboard (Story 73) */}
          <Route path="/executive-dashboard" element={<ExecutiveValueDashboard />} />

          {/* Business KPI Dashboard */}
          <Route path="/business-kpi" element={<BusinessKPIDashboard />} />

          {/* AI Factory Dashboard (Story 74) */}
          <Route path="/factory-dashboard" element={<AIFactoryDashboard />} />

          {/* Pipeline Tracker - Content Pipeline Mission Control */}
          <Route path="/pipeline-tracker" element={<PipelineTracker />} />

          {/* Content ROI Analytics (Story 369) */}
          <Route path="/content-roi" element={<ContentROIDashboard />} />

          {/* Content Intelligence Dashboard */}
          <Route path="/content-intelligence" element={<ContentIntelligence />} />
          {/* Content Opportunities — Consent Gate */}
          <Route path="/content-opportunities" element={<ContentOpportunities />} />

          {/* AI Share of Voice Dashboard */}
          <Route path="/sov-dashboard" element={<SOVDashboard />} />

          {/* Competitive Landscape — Actor model, categories, competitive position */}
          <Route path="/competitive-landscape" element={<CompetitiveLandscape />} />
          <Route path="/competitive-landscape/actor/:actorId" element={<ActorProfile />} />

          {/* Value Evidence Dashboard */}
          <Route path="/value-evidence" element={<ValueEvidenceDashboard />} />

          {/* Product Success Story Dashboard (Story 79) */}
          <Route path="/success-story/:productId" element={<ProductSuccessStory />} />

          {/* Deployment Verification */}
          <Route path="/deployment-verification" element={<DeploymentVerificationDashboard />} />

          {/* Research Intelligence */}
          <Route path="/research" element={<AEOResearch />} />
          <Route path="/research/aeo" element={<AEOResearch />} />
          <Route path="/research/question-wheel" element={<QuestionWheelResearch />} />
          <Route path="/question-wheels/:wheelId/debug" element={<QuestionWheelDebug />} />
          <Route path="/sectors/:sectorId/question-wheels/:wheelId/debug" element={<QuestionWheelDebug />} />
          <Route path="/research/intent-clusters" element={<CategoryIntentClusters />} />
          <Route path="/research/category-perceptions" element={<CategoryResearch />} />
          <Route path="/research/data-sources" element={<DataSources />} />
          <Route path="/research/attribute-approval" element={<AttributeApproval />} />
          <Route path="/research/enrichment-review" element={<EnrichmentReview />} />
          <Route path="/research/bulk-correction" element={<BulkCorrection />} />
          <Route path="/research/data-quality-audit" element={<DataQualityAudit />} />
          <Route path="/quality-audit" element={<QualityAuditDashboard />} />
          <Route path="/data-quality" element={<DataQualityDashboard />} />
          <Route path="/semantic-gaps" element={<SemanticGapsDashboard />} />
          <Route path="/translations" element={<TranslationsDashboard />} />
          <Route path="/research/benefit-mappings" element={<BenefitAttributeMappings />} />
          <Route path="/research/batch-management" element={<BatchResearchManagement />} />
          <Route path="/research/sector-wheels" element={<SectorQuestionWheels />} />
          <Route path="/sectors/:sectorId/batch-generation" element={<BatchQuestionGeneration />} />
          <Route path="/sectors/:sectorId/generate-from-category" element={<ProductCategoryGenerator />} />
          <Route path="/sectors/:sectorId/wheels/:wheelId/generate-product-questions" element={<QuestionGenerator />} />
          <Route path="/sites/:siteId/wheels/:wheelId/generate-product-questions" element={<QuestionGenerator />} />
          <Route path="/sectors/:sectorId/question-generation/monitor/:jobId" element={<BatchJobMonitor />} />
          <Route path="/sectors/:sectorId/operational-tools" element={<OperationalToolsDashboard />} />
          <Route path="/sectors/:sectorId/research-trends" element={<TrendResearchTool />} />
          <Route path="/research/documents" element={<ResearchDashboard />} />

          {/* Answer Generation & Knowledge Base */}
          <Route path="/answer-generation" element={<AnswerGeneration />} />
          <Route path="/answer-approval/:jobId" element={<AnswerApproval />} />
          <Route path="/ai-answer-test" element={<AIAnswerTest />} />
          <Route path="/knowledge-base" element={<KnowledgeBase />} />
          <Route path="/qa-system" element={<QASystemDashboard />} />

          {/* Demand Intelligence (Story 77) */}
          <Route path="/demand-intelligence" element={<DemandIntelligenceDashboard />} />

          {/* Demand Synthesis Layer */}
          <Route path="/demand-synthesis" element={<DemandSynthesisDashboard />} />

          {/* Terrain Explorer (zoomable D3 demand visualization) */}
          <Route path="/terrain-explorer" element={<TerrainExplorer />} />

          {/* AEO Readiness Dashboard */}
          <Route path="/aeo-readiness" element={<AEOReadinessDashboard />} />

          {/* AEO Onboarding Wizard */}
          <Route path="/aeo-onboarding" element={<AEOOnboardingWizard />} />

          {/* Analytics */}
          <Route path="/analytics" element={<MatomoAnalytics />} />
          <Route path="/analytics/matomo" element={<MatomoAnalytics />} />
          <Route path="/analytics/etl" element={<ETLMonitoring />} />
          <Route path="/analytics/customer-segments" element={<CustomerSegments />} />
          <Route path="/analytics/segment-management" element={<SegmentManagement />} />
          <Route path="/analytics/segment-analytics" element={<SegmentAnalytics />} />
          
          {/* Behavior */}
          <Route path="/behavior" element={<BehaviorDashboard />} />
          
          {/* Growth Intelligence */}
          <Route path="/growth" element={<GrowthDashboard />} />
          <Route path="/growth/thread/:type" element={<ThreadDetail />} />
          <Route path="/growth/competitors" element={<GrowthDashboard />} />
          <Route path="/growth/content-gaps" element={<GrowthDashboard />} />
          <Route path="/growth/journey" element={<GrowthDashboard />} />
          <Route path="/growth/metrics" element={<GrowthDashboard />} />
          <Route path="/growth/analyze" element={<GrowthDashboard />} />

          {/* Conversation Intelligence (Story: Category Conversation Intelligence) */}
          <Route path="/conversation-intelligence" element={<ConversationIntelligenceDashboard />} />

          {/* Session Replays */}
          <Route path="/session-replays" element={<SessionReplays />} />
          
          {/* Active Sessions (Super Admin) */}
          <Route path="/admin/active-sessions" element={<ActiveSessions />} />
          
          {/* Chatbot Logs (Super Admin) */}
          <Route path="/admin/chatbot-logs" element={<ChatbotLogs />} />
          
          {/* License Management (Super Admin) */}
          <Route path="/admin/licenses" element={<LicenseManagement />} />

          {/* Source Domain Management (Super Admin) */}
          <Route path="/admin/source-domains" element={<SourceDomainsManagement />} />
          
          {/* Chatbot Analytics */}
          <Route path="/chatbot-analytics" element={<ChatbotAnalytics />} />
          
          {/* Chatbot Performance (Super Admin) */}
          <Route path="/admin/chatbot-performance" element={<ChatbotPerformance />} />
          <Route path="/admin/search-performance" element={<SearchPerformance />} />

          {/* Discovery Orchestrator */}
          <Route path="/discovery" element={<DiscoveryPage />} />

          {/* Crew */}
          <Route path="/crew" element={<Crew />} />
          
          {/* Account */}
          <Route path="/account" element={<Account />} />
          <Route path="/account/licensing" element={<LicensingEnhanced />} />
          
          {/* Developer Tools (Super Admin) */}
          <Route path="/admin/developer-tools" element={<DeveloperTools />} />
          
          {/* Developer API Management (All Users) */}
          <Route path="/developer/api" element={<DeveloperTools />} />
          
          {/* Kafka Monitoring (Super Admin) */}
          <Route path="/admin/kafka-monitoring" element={<KafkaMonitoring />} />

          {/* Data Management (Super Admin) */}
          <Route path="/admin/data-management" element={<DataManagement />} />

          {/* Agent Conversations */}
          <Route path="/agent-conversations" element={<AgentConversationsPage />} />
          
          {/* Mock Agent Conversation */}
          <Route path="/mock-agent-conversation" element={<MockAgentConversation />} />
          
          {/* Platform Health (Super Admin) */}
          <Route path="/admin/grafana" element={
            <div className="space-y-6">
              <h1 className="text-3xl font-bold">Grafana Dashboard</h1>
              <div className="bg-card rounded-lg p-6">
                <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg mb-6">
                  <h3 className="font-semibold text-amber-900 mb-2">Grafana Access Notice</h3>
                  <p className="text-amber-800 mb-4">
                    Grafana cannot be embedded in iframes due to security restrictions (X-Frame-Options: deny).
                    You'll need to open Grafana in a new tab to access the monitoring dashboards.
                  </p>
                  <a 
                    href="http://localhost:3009" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Open Grafana Dashboard
                  </a>
                </div>
                <div className="text-sm text-muted-foreground">
                  <p className="mb-2"><strong>Default Credentials:</strong></p>
                  <p>Username: admin</p>
                  <p>Password: P@g3m1nd!</p>
                </div>
              </div>
            </div>
          } />
          <Route path="/admin/prometheus" element={
            <div className="space-y-6">
              <h1 className="text-3xl font-bold">Prometheus Metrics</h1>
              <div className="bg-card rounded-lg p-6 h-[80vh]">
                <iframe 
                  src="http://localhost:9090" 
                  className="w-full h-full border-0 rounded"
                  title="Prometheus"
                />
              </div>
            </div>
          } />
          
          {/* 404 Page */}
          <Route
            path="*"
            element={
              <div className="flex items-center justify-center h-[60vh]">
                <div className="text-center">
                  <h1 className="text-4xl font-bold">404</h1>
                  <h2 className="text-xl mt-2">Page not found</h2>
                  <p className="text-muted-foreground mt-1">
                    The page you are looking for does not exist.
                  </p>
                </div>
              </div>
            }
          />
          
          {/* Test routes */}
          <Route path="/test-reprocessing-dialog" element={<TestReprocessingDialog />} />
          <Route path="/streaming-chat-test" element={<StreamingChatTest />} />
          <Route path="/research/competitive-benchmark" element={<CompetitiveBenchmarkTest />} />

          {/* Help Center */}
          <Route path="/help" element={<HelpCenter />} />
          <Route path="/help/faq" element={<FAQ />} />
          <Route path="/help/guides" element={<UserGuides />} />
          <Route path="/help/guides/onboarding" element={<OnboardingGuide />} />
          <Route path="/help/guides/category-research" element={<CategoryResearchGuide />} />
          <Route path="/help/guides/google-integrations" element={<GoogleIntegrationsGuide />} />
        </Routes>
      </PageLayout>
        </LanguageProvider>
      </SiteProvider>
    </ProjectProvider>
  );
};

// Main App component with authentication routing
const App = () => {
  return (
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider defaultTheme="light" storageKey="pagemind-ui-theme">
          <AuthProvider>
            <TrackingProvider>
              <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/auth/signup" element={<SignUp />} />
              <Route path="/logout" element={<LogoutPage />} />
              
              {/* Public pages */}
              <Route path="/signup" element={<NewHomePage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/payment-success" element={<PaymentSuccessPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/features" element={<FeaturesPage />} />
              <Route path="/video-demo" element={<VideoDemo />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/partners" element={<PartnersPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/docs" element={<DocsPage />} />
              <Route path="/docs/feed-export" element={<FeedExportApiDocs />} />
              
              {/* Waitlist pages */}
              <Route path="/start" element={<WaitlistStartPage />} />
              <Route path="/add-site" element={<WaitlistAddSitePage />} />
              <Route path="/waitlist-confirmation" element={<WaitlistConfirmationPage />} />

              {/* Expert Interview Access (Public - token-based auth) */}
              <Route path="/interview/:interviewId" element={<ExpertInterview />} />

              {/* Public AEO Benchmark Tool (Lead Capture) */}
              <Route path="/benchmark" element={<AEOBenchmarkPublic />} />

              {/* Redirect root to signup */}
              <Route path="/" element={<Navigate to="/signup" replace />} />
              
              <Route
                path="/*"
                element={
                  <RequireAuth>
                    <AuthenticatedApp />
                  </RequireAuth>
                }
              />
            </Routes>
            </TrackingProvider>
          </AuthProvider>
          <Toaster />
        </ThemeProvider>
      </QueryClientProvider>
    </BrowserRouter>
  );
};

export default App;